import { useState, useCallback, useMemo, useEffect } from 'react';

/**
 * useSubformNavigation - Manages subform navigation state
 *
 * @param {Array} items - Array of items to navigate (subforms, sections, etc.)
 * @param {number} initialIndex - Initial active index (default: 0)
 * @returns {Object} - Navigation state and handlers
 */
const useSubformNavigation = (items = [], initialIndex = 0) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  // Clamp activeIndex when items change to prevent out-of-bounds indices
  useEffect(() => {
    if (items.length > 0 && activeIndex >= items.length) {
      setActiveIndex(0);
    }
  }, [items.length, activeIndex]);

  const navigate = useCallback(
    (index) => {
      if (index >= 0 && index < items.length) {
        setActiveIndex(index);
      }
    },
    [items.length]
  );

  const navigateNext = useCallback(() => {
    if (activeIndex < items.length - 1) {
      setActiveIndex((prev) => prev + 1);
    }
  }, [activeIndex, items.length]);

  const navigatePrev = useCallback(() => {
    if (activeIndex > 0) {
      setActiveIndex((prev) => prev - 1);
    }
  }, [activeIndex]);

  const reset = useCallback(() => {
    setActiveIndex(initialIndex);
  }, [initialIndex]);

  const activeItem = useMemo(
    () => (items.length > 0 ? items[activeIndex] : null),
    [items, activeIndex]
  );

  const hasMultipleItems = items.length > 1;
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === items.length - 1;

  return {
    activeIndex,
    activeItem,
    navigate,
    navigateNext,
    navigatePrev,
    reset,
    hasMultipleItems,
    isFirst,
    isLast,
    totalItems: items.length,
  };
};

export default useSubformNavigation;
